import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { GetCoursesParams, GetCoursesResponse } from '../interfaces/get-courses';
import { RequestError } from '../../../interfaces/error.interface';
import { GetCourseResponse } from '../interfaces/get-course';
import { Course } from '../../../interfaces/course.interface';
import { GetStudentsResponse } from '../interfaces/get-students';
import { CourseStudent } from '../../../interfaces/courseStudent.interface';
import { Category, GetCategoriesResponse } from '../interfaces/get-categories';
import { Module, ModuleClass } from '../../../interfaces/module.interface';
import { GetModulesResponse } from '../interfaces/get-modules';
import { Comment } from '../../../interfaces/comment.interface';
import { GetClassCommentsResponse } from '../interfaces/get-class-comments';
import { GetCourseViewsResponse } from '../interfaces/get-course-views';
import { PutModuleOrderPayloadRequest } from '../interfaces/put-module-order';
import { Rating } from '../../../interfaces/rating.interface';
import { GetRatingResponse } from '../interfaces/get-rating';
import { GetRatingsResponse } from '../interfaces/get-ratings';
import { GetChildCoursesResponse } from '../interfaces/get-child-courses';
import { GetCalculatorValuesParams, GetCalculatorValuesResponse, PositionCalculatorValues } from '../interfaces/get-calculator-values';
import { createEntityAdapter } from '../../../helpers/create-entity-adapter';
import { ForumSubject } from '../../../interfaces/forum.interface';
import { GetForumResponse } from '../interfaces/get-forum';
import { PutForumSubjectOrderPayloadRequest } from '../interfaces/put-forum-subject-order';
import { User } from '../../../interfaces/user.interface';
import { GetForumStudentsResponse } from '../interfaces/get-forum-students';
import { CalendarEvent } from '../../../interfaces/event.interface';
import { GetCalendarResponse } from '../interfaces/get-calendar';
import { Report } from '../../../interfaces/report.interface';
import { PostClassCommentSuccessPayload } from '../interfaces/post-comment';
import { GetReportResponse } from '../interfaces/get-report';
import { GetReportsResponse } from '../interfaces/get-reports';
import { Alliance } from '../../../interfaces/alliance.interface';
import { GetAlliancesResponse } from '../interfaces/get-alliances';

function addReplyToComments(comments: Comment[], parentId: string, reply: Comment): boolean {
  for (const c of comments) {
    if (c.id === parentId) {
      c.replies = [...(c.replies || []), reply];
      return true;
    }
    if (c.replies?.length && addReplyToComments(c.replies, parentId, reply)) {
      return true;
    }
  }
  return false;
}

export interface CourseState {
  courses: Course[];
  options: GetCoursesParams;
  course: Course | null;
  ratings: Rating[];
  rating: Rating | null;
  reports: Report[];
  report: Report | null;
  categories: Category[];
  modules: Module[];
  module: Module | null;
  students: CourseStudent[];
  childCourses: Course[];
  classComments: Comment[];
  forumComments: Comment[];
  forumSubjects: ForumSubject[];
  forumSubject: ForumSubject | null;
  calendarEvents: CalendarEvent[];
  readForumSubjects: string[];
  forumStudents: User[];
  viewed: string[];
  classCommentCount: number;
  forumCommentCount: number;
  positionCalculatorValues: {
    ids: string[];
    entities: Record<string, PositionCalculatorValues>;
  };
  positionCalculatorOptions: GetCalculatorValuesParams;
  total: number;
  page: number;
  noMore: boolean;
  selected: Course | null;
  currentClassId: string;
  expandedModules: string[];
  modulesStatus: 'idle' | 'loading' | 'success' | 'error';
  errorCode: number | null;
  alliances: Alliance[]
}

const initialState: CourseState = {
  courses: [],
  options: {},
  course: null,
  ratings: [],
  rating: null,
  reports: [],
  report: null,
  categories: [],
  modules: [],
  module: null,
  classComments: [],
  forumComments: [],
  forumSubjects: [],
  readForumSubjects: [],
  forumSubject: null,
  forumStudents: [],
  calendarEvents: [],
  viewed: [],
  classCommentCount: 0,
  forumCommentCount: 0,
  selected: null,
  students: [],
  positionCalculatorValues: {
    ids: [],
    entities: {},
  },
  positionCalculatorOptions: {},
  total: 0,
  page: 1,
  noMore: false,
  childCourses: [],
  currentClassId: '',
  expandedModules: [],
  modulesStatus: 'idle',
  errorCode: null,
  alliances: [],
};

const positionCalculatorValuesAdapter = createEntityAdapter<PositionCalculatorValues>((values: PositionCalculatorValues) => values.id);

const courseSlice = createSlice({
  name: 'course',
  initialState,
  reducers: {
    getCoursesInitApiAction: (state) => {
      state.errorCode = null;
    },
    getCoursesSuccessApiAction: (state, action: PayloadAction<GetCoursesResponse>) => {
      state.courses = action.payload.courses;
    },
    getCoursesFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getCourseInitApiAction: (state) => {
      state.errorCode = null;
    },
    getCourseSuccessApiAction: (state, action: PayloadAction<GetCourseResponse>) => {
      state.course = action.payload.course;
    },
    getCourseFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getStudentsInitApiAction: (state) => {
      state.errorCode = null;
    },
    getStudentsSuccessApiAction: (state, action: PayloadAction<GetStudentsResponse>) => {
      state.students = action.payload.students.filter(
        (student, index, students) =>
          students.findIndex((item) => item.user.id === student.user.id) === index,
      );
    },
    getStudentsFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getForumStudentsInitApiAction: (state) => {
      state.errorCode = null;
    },
    getForumStudentsSuccessApiAction: (state, action: PayloadAction<GetForumStudentsResponse>) => {
      state.forumStudents = action.payload.students;
    },
    getForumStudentsFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getModulesInitApiAction: (state) => {
      state.errorCode = null;
      state.modulesStatus = 'loading';
      state.modules = [];
    },
    getModulesSuccessApiAction: (state, action: PayloadAction<GetModulesResponse>) => {
      state.modules = action.payload.modules.map((module) => ({
        ...module,
        classes: module.classes.filter(
          (moduleClass, index, classes) =>
            classes.findIndex((item) => item.class.id === moduleClass.class.id) === index,
        ),
      }));
      state.modulesStatus = 'success';
    },
    getModulesFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
      state.modulesStatus = 'error';
    },
    addClassToModuleAction: (
      state,
      action: PayloadAction<{ moduleId: string; moduleClass: ModuleClass }>,
    ) => {
      const module = state.modules.find((item) => item.id === action.payload.moduleId);
      if (!module) {
        return;
      }

      const alreadyExists = module.classes.some(
        (moduleClass) => moduleClass.class.id === action.payload.moduleClass.class.id,
      );

      if (!alreadyExists) {
        module.classes = [action.payload.moduleClass, ...module.classes];
      }
    },
    removeClassFromModuleAction: (
      state,
      action: PayloadAction<{ moduleId: string; classId: string }>,
    ) => {
      const module = state.modules.find((item) => item.id === action.payload.moduleId);
      if (!module) {
        return;
      }

      module.classes = module.classes.filter(
        (moduleClass) => `${moduleClass.class.id}` !== `${action.payload.classId}`,
      );
    },
    getForumInitApiAction: (state) => {
      state.errorCode = null;
    },
    getForumSuccessApiAction: (state, action: PayloadAction<GetForumResponse>) => {
      state.forumSubjects = action.payload.subjects;
      state.readForumSubjects = action.payload.onlyRead;
    },
    getForumFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getCalendarInitApiAction: (state) => {
      state.errorCode = null;
    },
    getCalendarSuccessApiAction: (state, action: PayloadAction<GetCalendarResponse>) => {
      state.calendarEvents = action.payload.events;
    },
    getCalendarFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postCourseInitApiAction: () => {},
    postCourseSuccessApiAction: () => {},
    postCourseFailuireApiAction: () => {},
    putCourseInitApiAction: () => {},
    putCourseSuccessApiAction: () => {},
    putCourseFailuireApiAction: () => {},
    deleteCourseInitApiAction: () => {},
    deleteCourseSuccessApiAction: () => {},
    deleteCourseFailuireApiAction: () => {},
    postStudentInitApiAction: () => {},
    postStudentSuccessApiAction: () => {},
    postStudentFailuireApiAction: () => {},
    deleteStudentInitApiAction: () => {},
    deleteStudentSuccessApiAction: () => {},
    deleteStudentFailuireApiAction: () => {},
    postForumStudentInitApiAction: () => {},
    postForumStudentSuccessApiAction: () => {},
    postForumStudentFailuireApiAction: () => {},
    deleteForumStudentInitApiAction: () => {},
    deleteForumStudentSuccessApiAction: () => {},
    deleteForumStudentFailuireApiAction: () => {},
    postCalendarEventInitApiAction: () => {},
    postCalendarEventSuccessApiAction: () => {},
    postCalendarEventFailuireApiAction: () => {},
    postCalendarEmailInitApiAction: () => {},
    postCalendarEmailSuccessApiAction: () => {},
    postCalendarEmailFailuireApiAction: () => {},
    putCalendarEventInitApiAction: () => {},
    putCalendarEventSuccessApiAction: () => {},
    putCalendarEventFailuireApiAction: () => {},
    deleteCalendarEventInitApiAction: () => {},
    deleteCalendarEventSuccessApiAction: () => {},
    deleteCalendarEventFailuireApiAction: () => {},
    getCategoriesInitApiAction: (state) => {
      state.errorCode = null;
    },
    getCategoriesSuccessApiAction: (state, action: PayloadAction<GetCategoriesResponse>) => {
      state.categories = action.payload.categories;
    },
    getCategoriesFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postCategoryInitApiAction: () => {},
    postCategorySuccessApiAction: () => {},
    postCategoryFailuireApiAction: () => {},
    putCategoryInitApiAction: () => {},
    putCategorySuccessApiAction: () => {},
    putCategoryFailuireApiAction: () => {},
    deleteCategoryInitApiAction: () => {},
    deleteCategorySuccessApiAction: () => {},
    deleteCategoryFailuireApiAction: () => {},
    postModuleInitApiAction: () => {},
    postModuleSuccessApiAction: () => {},
    postModuleFailuireApiAction: () => {},
    putModuleInitApiAction: () => {},
    putModuleSuccessApiAction: () => {},
    putModuleFailuireApiAction: () => {},
    postForumSubjectInitApiAction: () => {},
    postForumSubjectSuccessApiAction: () => {},
    postForumSubjectFailuireApiAction: () => {},
    putForumSubjectInitApiAction: () => {},
    putForumSubjectSuccessApiAction: () => {},
    putForumSubjectFailuireApiAction: () => {},
    putForumSubjectOrderInitApiAction: (state, action: PayloadAction<PutForumSubjectOrderPayloadRequest>) => {
      state.errorCode = null;
      const subject = state.forumSubjects[action.payload.sIndex];
      const newArray = [...state.forumSubjects];
      newArray.splice(action.payload.sIndex, 1);
      newArray.splice(action.payload.dIndex, 0, subject);
      state.forumSubjects = newArray;
    },
    putForumSubjectOrderSuccessApiAction: () => {},
    putForumSubjectOrderFailuireApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postClassInitApiAction: () => {},
    postClassSuccessApiAction: () => {},
    postClassFailuireApiAction: () => {},
    deleteClassInitApiAction: () => {},
    deleteClassSuccessApiAction: () => {},
    deleteClassFailuireApiAction: () => {},
    postClassCommentInitApiAction: () => {},
    postClassCommentSuccessApiAction: (
      state,
      action: PayloadAction<PostClassCommentSuccessPayload | undefined>
    ) => {
      if (!action.payload) {
        return;
      }
      const { id, content, commentId, user } = action.payload;
      const newComment: Comment = {
        id,
        content,
        user,
        createdAt: new Date().toISOString(),
        attachments: [],
        replies: [],
      };
      if (commentId) {
        const ok = addReplyToComments(state.classComments, commentId, newComment);
        if (!ok) {
          state.classComments = [newComment, ...state.classComments];
        }
      } else {
        state.classComments = [newComment, ...state.classComments];
      }
      state.classCommentCount += 1;
    },
    postClassCommentFailuireApiAction: () => {},
    getClassCommentsInitApiAction: (state) => {
      state.errorCode = null;
    },
    getClassCommentsSuccessApiAction: (state, action: PayloadAction<GetClassCommentsResponse>) => {
      state.classComments = action.payload.comments;
      state.classCommentCount = action.payload.count;
    },
    getClassCommentsFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    deleteClassCommentInitApiAction: () => {},
    deleteClassCommentSuccessApiAction: () => {},
    deleteClassCommentFailuireApiAction: () => {},
    postForumCommentInitApiAction: () => {},
    postForumCommentSuccessApiAction: () => {},
    postForumCommentFailuireApiAction: () => {},
    getForumCommentsInitApiAction: (state) => {
      state.errorCode = null;
    },
    getForumCommentsSuccessApiAction: (state, action: PayloadAction<GetClassCommentsResponse>) => {
      state.forumComments = action.payload.comments;
      state.forumCommentCount = action.payload.count;
    },
    getForumCommentsFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    deleteForumCommentInitApiAction: () => {},
    deleteForumCommentSuccessApiAction: () => {},
    deleteForumCommentFailuireApiAction: () => {},
    getViewsInitApiAction: (state) => {
      state.errorCode = null;
    },
    getViewsSuccessApiAction: (state, action: PayloadAction<GetCourseViewsResponse>) => {
      state.viewed = action.payload.views;
    },
    getViewsFailuireApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postViewInitApiAction: () => {},
    postViewSuccessApiAction: () => {},
    postViewFailuireApiAction: () => {},
    deleteViewInitApiAction: () => {},
    deleteViewSuccessApiAction: () => {},
    deleteViewFailuireApiAction: () => {},
    putModuleOrderInitApiAction: (state, action: PayloadAction<PutModuleOrderPayloadRequest>) => {
      state.errorCode = null;
      const module = state.modules[action.payload.sIndex];
      const newArray = [...state.modules];
      newArray.splice(action.payload.sIndex, 1);
      newArray.splice(action.payload.dIndex, 0, module);
      state.modules = newArray;
    },
    putModuleOrderSuccessApiAction: () => {},
    putModuleOrderFailuireApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    putClassOrderInitApiAction: (state, action: PayloadAction<{ id: string, body: PutModuleOrderPayloadRequest}>) => {
      state.errorCode = null;
      const moduleIndex = state.modules.findIndex(
        (module) => `${module.id}` === `${action.payload.id}`,
      );
      if (moduleIndex === -1) {
        return;
      }

      const module = state.modules[moduleIndex];
      const clazz = module.classes[action.payload.body.sIndex];
      const newArray = [...module.classes];
      newArray.splice(action.payload.body.sIndex, 1);
      newArray.splice(action.payload.body.dIndex, 0, clazz);
      state.modules[moduleIndex].classes = newArray;
    },
    putClassOrderSuccessApiAction: () => {},
    putClassOrderFailuireApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postRatingInitApiAction: () => {},
    postRatingSuccessApiAction: () => {},
    postRatingFailuireApiAction: () => {},
    getRatingsInitApiAction: (state) => {
      state.errorCode = null;
    },
    getRatingsSuccessApiAction: (state, action: PayloadAction<GetRatingsResponse>) => {
      state.ratings = action.payload.ratings;
    },
    getRatingsFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getRatingInitApiAction: (state) => {
      state.errorCode = null;
    },
    getRatingSuccessApiAction: (state, action: PayloadAction<GetRatingResponse>) => {
      state.rating = action.payload.rating;
    },
    getRatingFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getChildCoursesInitApiAction: (state) => {
      state.errorCode = null;
    },
    getChildCoursesSuccessApiAction: (state, action: PayloadAction<GetChildCoursesResponse>) => {
      state.childCourses = action.payload.courses;
    },
    getChildCoursesFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postChildCourseInitApiAction: () => {},
    postChildCourseSuccessApiAction: () => {},
    postChildCourseFailuireApiAction: () => {},
    deleteChildCourseInitApiAction: () => {},
    deleteChildCourseSuccessApiAction: () => {},
    deleteChildCourseFailuireApiAction: () => {},
    deleteModulesInitApiActions: () => {},
    deleteModulesSuccessApiActions: () => {},
    deleteModulesFailureApiActions: () => {},
    deleteForumSubjectInitApiActions: () => {},
    deleteForumSubjectSuccessApiActions: () => {},
    deleteForumSubjectFailureApiActions: () => {},
    getStudentsFileInitApiAction: (state) => {
      state.errorCode = null;
    },
    getStudentsFileSuccessApiAction: () => {},
    getStudentsFileFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postCalculatorValuesInitApiAction: () => {},
    postCalculatorValuesSuccessApiAction: () => {},
    postCalculatorValuesFailuireApiAction: () => {},
    getPositionCalculatorValuesInitApiAction: (state) => {
      state.errorCode = null;
    },
    getPositionCalculatorValuesSuccessApiAction: (state, action: PayloadAction<GetCalculatorValuesResponse>) => {
      positionCalculatorValuesAdapter.updateMany(action.payload.values, state.positionCalculatorValues);
      state.noMore = action.payload.values.length < 50;
    },
    getPositionCalculatorValuesFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getDownloadFileInitApiAction: (state) => {
      state.errorCode = null;
    },
    getDownloadFileSuccessApiAction: () => {},
    getDownloadFileFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getReportInitApiAction: (state) => {
      state.errorCode = null;
    },
    getReportSuccessApiAction: (state, action: PayloadAction<GetReportResponse>) => {
      state.report = action.payload.report;
    },
    getReportFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getAlliancesInitApiAction: (state) => {
      state.errorCode = null;
    },
    getAlliancesSuccessApiAction: (state, action: PayloadAction<GetAlliancesResponse>) => {
      state.alliances = action.payload.alliances;
    },
    getAlliancesFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getReportsInitApiAction: (state) => {
      state.errorCode = null;
    },
    getReportsSuccessApiAction: (state, action: PayloadAction<GetReportsResponse>) => {
      state.reports = action.payload.reports;
    },
    getReportsFailureApiAction: (state,action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    deleteReportInitApiAction: () => {},
    deleteReportSuccessApiAction: () => {},
    deleteReportFailureApiAction: () => {},
    deleteAllianceInitApiAction: () => {},
    deleteAllianceSuccessApiAction: () => {},
    deleteAllianceFailureApiAction: () => {},
    postAllianceInitApiAction: () => {},
    postAllianceSuccessApiAction: () => {},
    postAllianceFailureApiAction: () => {},
    postReportInitApiAction: () => {},
    postReportSuccessApiAction: () => {},
    postReportFailureApiAction: () => {},
    setCourseAction: (state, action: PayloadAction<Course>) => {
      state.selected = action.payload;
    },
    setModuleAction: (state, action: PayloadAction<Module>) => {
      state.module = action.payload;
    },
    setSubjectAction: (state, action: PayloadAction<ForumSubject | null>) => {
      state.forumSubject = action.payload;
    },
    setOptionsAction: (state, action: PayloadAction<GetCoursesParams>) => {
      state.options = action.payload;
    },
    resetOptionsAction: (state) => {
      state.options = {};
    },
    increasePageAction: (state) => {
      state.page += 1;
    },
    setCalculatorOptionsAction: (state, action: PayloadAction<GetCalculatorValuesParams>) => {
      positionCalculatorValuesAdapter.removeAll(state.positionCalculatorValues);
      state.positionCalculatorOptions = action.payload;
      state.page = 1;
    },
    setCurrentClassIdAction: (state, action: PayloadAction<string>) => {
      state.currentClassId = action.payload;
    },
    // Abre sin alternar: al entrar a una clase el módulo debe quedar abierto aunque
    // ya lo estuviera (alternar lo cerraba al llegar desde notificaciones).
    expandModuleAction: (state, action: PayloadAction<string>) => {
      if (!state.expandedModules.includes(action.payload)) {
        state.expandedModules = [...state.expandedModules, action.payload];
      }
    },
    toggleExpandedModuleAction: (state, action: PayloadAction<string>) => {
      if (state.expandedModules.includes(action.payload)) {
        state.expandedModules = state.expandedModules.filter(moduleId => moduleId !== action.payload);
      } else {
        state.expandedModules = [...state.expandedModules, action.payload];
      }
    },
    resetCalculatorOptionsAction: (state) => {
      state.positionCalculatorOptions = {};
    },
    calculatorClearAction: (state) => {
      positionCalculatorValuesAdapter.removeAll(state.positionCalculatorValues);
      state.options = {};
      state.page = 1;
      state.total = 0;
      state.noMore = false;
    },
    courseClearAction: (state) => {
      state.courses = [];
      state.modules = [];
      state.modulesStatus = 'idle';
      state.module = null;
      state.classComments = [];
      state.forumComments = [];
      state.forumSubjects = [];
      state.classCommentCount = 0;
      state.forumCommentCount = 0;
      state.calendarEvents = [];
      state.page = 1;
      state.options = {};
      state.course = null;
      state.selected = null;
      state.childCourses = [];
      state.students = [];
      state.options = {};
      state.positionCalculatorOptions = {};
      state.errorCode = null;
    },
  },
});

export const {
  getCoursesInitApiAction,
  getCoursesSuccessApiAction,
  getCoursesFailureApiAction,
  getCourseInitApiAction,
  getCourseSuccessApiAction,
  getCourseFailureApiAction,
  getStudentsInitApiAction,
  getStudentsSuccessApiAction,
  getStudentsFailureApiAction,
  postCourseInitApiAction,
  postCourseSuccessApiAction,
  postCourseFailuireApiAction,
  putCourseInitApiAction,
  putCourseSuccessApiAction,
  putCourseFailuireApiAction,
  deleteCourseInitApiAction,
  deleteCourseSuccessApiAction,
  deleteCourseFailuireApiAction,
  postStudentInitApiAction,
  postStudentSuccessApiAction,
  postStudentFailuireApiAction,
  deleteStudentInitApiAction,
  deleteStudentSuccessApiAction,
  deleteStudentFailuireApiAction,
  getCategoriesInitApiAction,
  getCategoriesSuccessApiAction,
  getCategoriesFailureApiAction,
  postCategoryInitApiAction,
  postCategorySuccessApiAction,
  postCategoryFailuireApiAction,
  putCategoryInitApiAction,
  putCategorySuccessApiAction,
  putCategoryFailuireApiAction,
  deleteCategoryInitApiAction,
  deleteCategorySuccessApiAction,
  deleteCategoryFailuireApiAction,
  getModulesInitApiAction,
  getModulesSuccessApiAction,
  getModulesFailureApiAction,
  addClassToModuleAction,
  removeClassFromModuleAction,
  postModuleInitApiAction,
  postModuleSuccessApiAction,
  postModuleFailuireApiAction,
  putModuleInitApiAction,
  putModuleSuccessApiAction,
  putModuleFailuireApiAction,
  postForumSubjectInitApiAction,
  postForumSubjectSuccessApiAction,
  postForumSubjectFailuireApiAction,
  putForumSubjectInitApiAction,
  putForumSubjectSuccessApiAction,
  putForumSubjectFailuireApiAction,
  deleteForumSubjectInitApiActions,
  deleteForumSubjectSuccessApiActions,
  deleteForumSubjectFailureApiActions,
  putForumSubjectOrderInitApiAction,
  putForumSubjectOrderSuccessApiAction,
  putForumSubjectOrderFailuireApiAction,
  getDownloadFileInitApiAction,
  getDownloadFileSuccessApiAction,
  getDownloadFileFailureApiAction,
  postClassInitApiAction,
  postClassSuccessApiAction,
  postClassFailuireApiAction,
  deleteClassInitApiAction,
  deleteClassSuccessApiAction,
  deleteClassFailuireApiAction,
  postClassCommentInitApiAction,
  postClassCommentSuccessApiAction,
  postClassCommentFailuireApiAction,
  getClassCommentsInitApiAction,
  getClassCommentsSuccessApiAction,
  getClassCommentsFailureApiAction,
  deleteClassCommentInitApiAction,
  deleteClassCommentSuccessApiAction,
  deleteClassCommentFailuireApiAction,
  postForumCommentInitApiAction,
  postForumCommentSuccessApiAction,
  postForumCommentFailuireApiAction,
  getForumCommentsInitApiAction,
  getForumCommentsSuccessApiAction,
  getForumCommentsFailureApiAction,
  deleteForumCommentInitApiAction,
  deleteForumCommentSuccessApiAction,
  deleteForumCommentFailuireApiAction,
  getForumStudentsInitApiAction,
  getForumStudentsSuccessApiAction,
  getForumStudentsFailureApiAction,
  postForumStudentInitApiAction,
  postForumStudentSuccessApiAction,
  postForumStudentFailuireApiAction,
  deleteForumStudentInitApiAction,
  deleteForumStudentSuccessApiAction,
  deleteForumStudentFailuireApiAction,
  getViewsInitApiAction,
  getViewsSuccessApiAction,
  getViewsFailuireApiAction,
  putModuleOrderInitApiAction,
  putModuleOrderSuccessApiAction,
  putModuleOrderFailuireApiAction,
  putClassOrderInitApiAction,
  putClassOrderSuccessApiAction,
  putClassOrderFailuireApiAction,
  postViewInitApiAction,
  postViewSuccessApiAction,
  postViewFailuireApiAction,
  getRatingsInitApiAction,
  getRatingsSuccessApiAction,
  getRatingsFailureApiAction,
  getRatingInitApiAction,
  getRatingSuccessApiAction,
  getRatingFailureApiAction,
  postRatingInitApiAction,
  postRatingSuccessApiAction,
  postRatingFailuireApiAction,
  deleteViewInitApiAction,
  deleteViewSuccessApiAction,
  deleteViewFailuireApiAction,
  getChildCoursesInitApiAction,
  getChildCoursesSuccessApiAction,
  getChildCoursesFailureApiAction,
  postChildCourseInitApiAction,
  postChildCourseSuccessApiAction,
  postChildCourseFailuireApiAction,
  deleteChildCourseInitApiAction,
  deleteChildCourseSuccessApiAction,
  deleteChildCourseFailuireApiAction,
  deleteModulesInitApiActions,
  deleteModulesSuccessApiActions,
  deleteModulesFailureApiActions,
  getForumInitApiAction,
  getForumSuccessApiAction,
  getForumFailureApiAction,
  getStudentsFileInitApiAction,
  getStudentsFileSuccessApiAction,
  getStudentsFileFailureApiAction,
  postCalculatorValuesInitApiAction,
  postCalculatorValuesSuccessApiAction,
  postCalculatorValuesFailuireApiAction,
  getPositionCalculatorValuesInitApiAction,
  getPositionCalculatorValuesSuccessApiAction,
  getPositionCalculatorValuesFailureApiAction,
  getCalendarInitApiAction,
  getCalendarSuccessApiAction,
  getCalendarFailureApiAction,
  postCalendarEventInitApiAction,
  postCalendarEventSuccessApiAction,
  postCalendarEventFailuireApiAction,
  putCalendarEventInitApiAction,
  putCalendarEventSuccessApiAction,
  putCalendarEventFailuireApiAction,
  deleteCalendarEventInitApiAction,
  deleteCalendarEventSuccessApiAction,
  deleteCalendarEventFailuireApiAction,
  getReportsInitApiAction,
  getReportsSuccessApiAction,
  getReportsFailureApiAction,
  getReportInitApiAction,
  getReportSuccessApiAction,
  getReportFailureApiAction,
  postReportInitApiAction,
  postReportSuccessApiAction,
  postReportFailureApiAction,
  deleteReportInitApiAction,
  deleteReportSuccessApiAction,
  deleteAllianceInitApiAction,
  deleteAllianceSuccessApiAction,
  deleteAllianceFailureApiAction,
  getAlliancesInitApiAction,
  getAlliancesSuccessApiAction,
  getAlliancesFailureApiAction,
  postAllianceInitApiAction,
  postAllianceSuccessApiAction,
  postAllianceFailureApiAction,
  deleteReportFailureApiAction,
  postCalendarEmailInitApiAction,
  postCalendarEmailSuccessApiAction,
  postCalendarEmailFailuireApiAction,
  setCurrentClassIdAction,
  toggleExpandedModuleAction,
  expandModuleAction,
  increasePageAction,
  setCalculatorOptionsAction,
  setSubjectAction,
  resetCalculatorOptionsAction,
  calculatorClearAction,
  setModuleAction,
  setCourseAction,
  setOptionsAction,
  resetOptionsAction,
  courseClearAction,
} = courseSlice.actions;

export default courseSlice.reducer;
